import {SectionAnswerMessage, matchingAnswerMessages} from '../models/answer-messages';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {QuestionControlService} from "../services/question-control.service";
import {QuestionBase, Section} from "../models/question-base";
import {AdvFormService} from "../services/adv-form.service";
import {empty, Subscription} from "rxjs";
import {Router} from "@angular/router";
import {sectionFunction} from "./shared/sectionFunction";
import {TranslateService} from "@ngx-translate/core";
import {ApiUrl} from "../../../shared/models/apiurl.models";
import {PathOptions, selectOptionData} from "../dynamic-form-question/dynamic-form-question.component";
import {HttpClient} from "@angular/common/http";
import {MatInput} from "@angular/material/input";
import {FloatingNotificationService} from "../../../shared/floating-notification/floating-notification.service";
import {isSuppressedBy} from '../models/required-unless';


export interface SectionUI {
    description: string;
    number: number;
    namespace: string;
    sectionKey: string,
    expanded: boolean;
    overflow: boolean;
    callBackFunction: string;
    questions: QuestionBase<String>[];
    requiredQuestion: QuestionBase<string> [];
    // SCRUM-154: optional guidance text + "upgrade your ad" banner per section.
    helpText?: string | null;
    showUpgradeBanner?: boolean;
    upgradeBannerTitle?: string | null;
    upgradeBannerText?: string | null;
    // SCRUM-231: messages shown when a question here gets a given answer.
    answerMessages?: SectionAnswerMessage[];
}

@Component({
    selector: 'app-dynamic-form',
    styleUrls: ['./dynamic-form.component.css'],
    templateUrl: './dynamic-form.component.html',
    providers: [QuestionControlService],
    standalone: false
})
export class DynamicFormComponent implements OnInit, OnDestroy {

    @Input() questions: QuestionBase<string>[] | null = [];
    form!: FormGroup;
    payLoad = '';
    sections!: SectionUI[];
    sectionMissingFlg: boolean[] = [];
    sectionDescription: string[] = [];
    greenPass: boolean = false;
    @Input() expandFlag: boolean = false;
    navigationOrderByKey = new Map<string, number>();
    private lastNavigationOrder = -1;
    private navigationInProgress = false;
    private answerEvents?: Subscription;
    // SCRUM-154: the schema's own `required` for bindSelect questions, which
    // initBindSelect() toggles at runtime.
    private bindSelectBaseRequired = new Map<string, boolean>();

    constructor(private qcs: QuestionControlService, private formService: AdvFormService, private router: Router, private translateService: TranslateService, private apiUrl: ApiUrl, private http: HttpClient, private notificationService: FloatingNotificationService) {
        const mainCategory = this.formService.getQuestion('mainCategory')
        if (mainCategory == undefined)
            this.router.navigate(['/publish']);

        // this.formService.getClickEvent().subscribe(res => {})

        this.answerEvents = this.formService.getQOClickEvent().subscribe(res=>{
            this.setMissingRequiredData();
            this.setTabDescription();
            if(['images', 'userContactDetails','armatureList'].indexOf(res.index) ==-1 ){
                this.closeTabIfCompleted();
            }
            this.greenPass = this.setGreenPass();
        })
    }


    ngOnDestroy(): void {
        // SCRUM-154: the form can now be re-created in place (sub-category
        // overload), so a destroyed instance must stop reacting to answers.
        this.answerEvents?.unsubscribe();
    }

    /**
     * SCRUM-154: a question is suppressed (hidden + not required) while the
     * checkbox named in `requiredUnless` is ticked — e.g. "Test until" once
     * "No test" is ticked.
     */
    isSuppressed(question: QuestionBase<any>): boolean {
        return isSuppressedBy(question?.requiredUnless, key => this.formService.getQuestion(key));
    }

    /** SCRUM-154: a bindSelect question is only visible when its list has values. */
    private isHiddenBindSelect(question: QuestionBase<any>): boolean {
        return question?.type === 'bindSelect' && !question.bindSelectHide;
    }

    private refreshAfterBindSelect(): void {
        this.setMissingRequiredData();
        this.setTabDescription();
        this.greenPass = this.computeGreenPass();
    }

    /**
     * SCRUM-231: the section's answer-based messages whose question currently
     * has a matching answer (e.g. קטגוריה = "כלי נשק").
     */
    answerMessagesFor(section: SectionUI): SectionAnswerMessage[] {
        if (!section?.answerMessages?.length) return [];
        return matchingAnswerMessages(section.answerMessages, key => this.formService.getQuestion(key));
    }

    scrollToRoutes(): void {
        document.querySelector<HTMLElement>('.dynamic-form .route-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }

    initBindSelect() {
        this.questions.filter(ques => ques.type === "bindSelect").forEach(qs => {
            if (!this.bindSelectBaseRequired.has(qs.key)) {
                this.bindSelectBaseRequired.set(qs.key, qs.required);
            }
            const baseRequired = this.bindSelectBaseRequired.get(qs.key) === true;
            const quesParam = this.apiUrl.pathTranslate(qs.callBackFunction, this.formService);
            let isDisplay = true;
            quesParam.requiredField.forEach(res => {
                if (res.value != '' && res.value != null) {
                    isDisplay = false;
                }
            })
            if (!isDisplay) {
                this.http.get<selectOptionData[]>(quesParam.path).subscribe((res: selectOptionData[]) => {
                        if (res != null && res.length > 0) {
                            qs.bindSelectHide = !isDisplay;
                            // SCRUM-154: visible -> the schema decides (e.g. "Model *").
                            qs.required = baseRequired;
                        } else {
                            qs.bindSelectHide = isDisplay;
                            // SCRUM-154: hidden -> never required.
                            qs.required = false;
                        }
                        this.refreshAfterBindSelect();
                    }
                )
            }else{
                qs.bindSelectHide = !isDisplay;
                qs.required = false;
            }

        })
    }

    setGreenPass() {
        this.initBindSelect();
        return this.computeGreenPass();
    }

    private computeGreenPass() {
        let flg = true;
        this.sectionMissingFlg.forEach(res => {
            if (typeof res == "boolean") {
                if (res == true) {
                    flg = false;
                }
            }
        });
        return flg;
    }

    setSectionQuestions(questions: QuestionBase<string>[] | null): SectionUI[] {
        let section: SectionUI[] = [];
        let SectionMap = new Map();
        questions?.forEach((res: QuestionBase<string>) => {
            // @ts-ignore
            var qS: SectionUI = res.section
            if (SectionMap.get(qS.number) == undefined) {
                SectionMap.set(qS.number, {
                    description: qS.description,
                    helpText: qS.helpText,
                    showUpgradeBanner: qS.showUpgradeBanner,
                    upgradeBannerTitle: qS.upgradeBannerTitle,
                    upgradeBannerText: qS.upgradeBannerText,
                    answerMessages: qS.answerMessages ?? [],
                    number: qS.number,
                    namespace: qS.namespace,
                    sectionKey: qS.sectionKey,
                    expanded: qS.expanded,
                    questions: [],
                    requiredQuestion: [],
                    callBackFunction: qS.callBackFunction
                })
            }
            SectionMap.get(qS.number).questions.push(res);
            if (res.required === true) {
                SectionMap.get(qS.number).requiredQuestion.push(res);
            }
        })
        SectionMap.forEach((value, index) => {
            section.push(value);
        })
        return section;
    }

    private getDescriptionTab(section: SectionUI) {
        const selectedValue = section.questions.map((q: any) => this.formService.getQuestion(q.key))
        if (section.callBackFunction != null && Object.keys(sectionFunction).indexOf(section.callBackFunction) != -1) {
            return sectionFunction[section.callBackFunction](selectedValue, this.translateService);
        } else {
            return sectionFunction['generalFunction'](selectedValue, this.translateService);
        }
    }

    scrollToTop() {
        const target = document.getElementById("formBoxId");
        target?.scrollIntoView({behavior: 'smooth'});
    }

    closeTabIfCompleted() {
        let openedSections = this.sections.find(sec => sec.expanded)
        let completed = true;
        let monitor: any[] = []
        if (openedSections && openedSections?.questions.length > 0) {
            openedSections?.questions.forEach(q => {
                monitor[q['key']] = this.formService.getQuestion(q['key'])
            })

        }
        if (Object.values(monitor).indexOf(undefined) == -1) {
            completed = false
        }
        if (openedSections && !completed) {
            openedSections.expanded = completed;
            this.scrollToTop()
        }
    }

    setTabDescription() {
        this.sections.forEach(sec => {
            if (!this.sectionMissingFlg[sec.number]) {
                this.sectionDescription[sec.number] = this.getDescriptionTab(sec);
            } else {
                this.sectionDescription[sec.number] = ""
            }

        })
    }

    setMissingRequiredData() {
        this.sections.forEach(sec => {
            this.sectionMissingFlg[sec.number] = false
            if (sec.requiredQuestion.length == 0) {
                this.sectionMissingFlg[sec.number] =
                    sec.questions.some(q => q.key === 'userContactDetails') && !this.formService.isContactComplete();

            } else {
                sec.requiredQuestion.forEach(rQ => {
                    // SCRUM-154: hidden questions can't block the section.
                    if (this.isSuppressed(rQ) || this.isHiddenBindSelect(rQ)) return;
                    if (this.formService.getQuestion(rQ.key) === undefined) {
                        this.sectionMissingFlg[sec.number] = true;
                    }
                    // Contact details: phone filled + verified + terms
                    // approved, not just "something was entered".
                    if (rQ.key === 'userContactDetails' && !this.formService.isContactComplete()) {
                        this.sectionMissingFlg[sec.number] = true;
                    }
                })
            }
        })

    }

    ngOnInit() {
        this.sections = this.setSectionQuestions(this.questions).sort(function (a, b) {
            return a.number === b.number ? 0 : a.number < b.number ? -1 : 1;
        });
        this.buildNavigationOrder();

        this.setMissingRequiredData();
        this.form = this.qcs.toFormGroup(this.questions as QuestionBase<string>[]);
        // Manual and Adaline modes share AdvFormService. Hydrate both the
        // reactive controls and question values when the user switches modes.
        this.questions?.forEach(question => {
            const storedValue = this.formService.getQuestion(question.key);
            if (storedValue !== undefined) {
                question.value = storedValue;
                this.form.get(question.key)?.setValue(storedValue, {emitEvent: false});
            }
        });
        this.skipPresetOnlySection();
        this.resumeAtSharedPosition();
    }

    /**
     * SCRUM-276: when the panel the server opens first holds only locked,
     * preset questions (נדל"ן ומסחרי -> sub-category מסחרי), there is nothing
     * to do in it - open the first panel that has a question to answer.
     */
    private skipPresetOnlySection(): void {
        const opened = this.sections.find(section => section.expanded);
        if (!opened) return;
        const questions = this.getQuestions(opened.number) || [];
        if (!questions.length || questions.some(question => !question.disabled)) return;
        const next = this.sections.find(section =>
            (this.getQuestions(section.number) || []).some(question => !question.disabled));
        if (!next) return;
        this.sections.forEach(section => section.expanded = section === next);
    }

    onSubmit() {
        this.payLoad = JSON.stringify(this.form.getRawValue());
    }

    getQuestions(stage: number) {
        return this.questions?.filter(res => res.page === stage)
    }

    openTab($event: any) {
        this.expandFlag = false;
    }

    expandWindow(section: any) {
        this.expandFlag = true;
        setTimeout(() => this.expandFlag = false, 250)
        this.notificationService.showByCode(section.sectionKey)
        this.sections.map((res: SectionUI) => {
            if (section == res) {
                res.expanded = true
            } else {
                res.expanded = false
            }
        })
    }

    submitAdv() {

    }

    onToggle() {

    }

    onPanelOpened(section: SectionUI) {
        if (!section) {
            return;
        }
        section.expanded = true;
    }

    onPanelClosed(section: SectionUI) {
        if (!section) {
            return;
        }
        section.expanded = false;
    }

    focusNextEmptyField(section: SectionUI): void {
        if (this.navigationInProgress) return;
        // afterExpand fires once Material has finished opening the panel. A
        // short frame delay lets nested dynamic controls finish rendering.
        setTimeout(() => {
            const panel = document.querySelector<HTMLElement>(
                `.sectionBox[data-section-number="${section.number}"]`
            );
            const body = panel?.querySelector<HTMLElement>('.body-content');
            if (!body) return;

            const activeElement = document.activeElement as HTMLElement | null;
            if (activeElement && body.contains(activeElement)) return;

            const selector = [
                'input.input-field-focus:not([disabled])',
                'textarea.input-field-focus:not([disabled])',
                'select.input-field-focus:not([disabled])',
                'input.form-control:not([type="checkbox"]):not([disabled])',
                'input.modern-checkbox-input:not([disabled])',
                'input.consent-input:not([disabled])',
                'button.categoryBox:not([disabled])'
            ].join(',');

            const controls = Array.from(body.querySelectorAll<HTMLElement>(selector));
            const nextEmpty = controls.find(control => this.isEmptyVisibleControl(control));
            if (!nextEmpty) return;

            nextEmpty.focus({preventScroll: true});
            nextEmpty.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }, 40);
    }

    navigateToNextField(): void {
        const activeElement = document.activeElement as HTMLElement | null;
        const activeQuestion = activeElement?.closest<HTMLElement>('[data-navigation-order]');
        const activeOrder = Number(activeQuestion?.dataset['navigationOrder']);
        const currentOrder = Number.isFinite(activeOrder) ? activeOrder : this.lastNavigationOrder;
        const orderedQuestions = Array.from(
            document.querySelectorAll<HTMLElement>('#formBoxId .navigation-question[data-navigation-order]')
        )
            .filter(container => Number(container.dataset['navigationOrder']) >= 0)
            .filter(container => Boolean(
                container.matches('.license-panel') || container.querySelector('app-question')
            ))
            .sort((a, b) =>
                Number(a.dataset['navigationOrder']) - Number(b.dataset['navigationOrder'])
            );
        const target = orderedQuestions.find(container =>
            Number(container.dataset['navigationOrder']) > currentOrder
        );

        // Once the user has visited the final field, continue naturally to
        // route selection instead of cycling back to the beginning.
        if (!target) {
            this.lastNavigationOrder = -1;
            document.querySelector<HTMLElement>('.dynamic-form .route-section')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            return;
        }

        const targetOrder = Number(target.dataset['navigationOrder']);
        const targetPanel = target.closest<HTMLElement>('.sectionBox[data-section-number]');
        const targetSectionNumber = Number(targetPanel?.dataset['sectionNumber']);
        const targetSection = this.sections.find(section => section.number === targetSectionNumber);
        this.lastNavigationOrder = targetOrder;
        this.navigationInProgress = true;
        if (targetSection) {
            this.sections.forEach(section => section.expanded = section === targetSection);
        }

        // Wait for Material's panel animation and conditional controls to render,
        // then position the question before focusing its real interactive control.
        window.setTimeout(() => {
            const container = Array.from(
                document.querySelectorAll<HTMLElement>('#formBoxId .navigation-question[data-navigation-order]')
            ).find(element => Number(element.dataset['navigationOrder']) === targetOrder);
            if (!container) {
                this.navigationInProgress = false;
                if (targetSection) this.focusNextEmptyField(targetSection);
                return;
            }

            container.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
            const control = container.querySelector<HTMLElement>([
                'input:not([disabled]):not([type="hidden"])',
                'textarea:not([disabled])',
                'select:not([disabled])',
                'button:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ].join(','));
            control?.focus({preventScroll: true});
            this.navigationInProgress = false;
        }, 280);
    }

    rememberNavigationPosition(event: FocusEvent): void {
        const target = event.target as HTMLElement | null;
        const container = target?.closest<HTMLElement>('[data-navigation-order]');
        const order = Number(container?.dataset['navigationOrder']);
        if (Number.isFinite(order)) {
            this.lastNavigationOrder = order;
            const questionKey = [...this.navigationOrderByKey.entries()]
                .find(([, navigationOrder]) => navigationOrder === order)?.[0];
            if (questionKey) this.formService.setActiveQuestion(questionKey);
        }
    }

    private resumeAtSharedPosition(): void {
        const questionKey = this.formService.getActiveQuestion();
        if (!questionKey) return;

        const question = this.questions?.find(item => item.key === questionKey);
        const sectionNumber = Number((question?.section as any)?.number);
        const section = this.sections.find(item => item.number === sectionNumber);
        if (!question || !section) return;

        this.sections.forEach(item => item.expanded = item === section);
        window.setTimeout(() => {
            const questionContainers = Array.from(
                document.querySelectorAll<HTMLElement>('.dynamic-form [data-question-key]')
            );
            const questionContainer = questionContainers.find(
                container => container.dataset['questionKey'] === questionKey
            );
            const sectionContainer = document.querySelector<HTMLElement>(
                `.dynamic-form .sectionBox[data-section-number="${section.number}"]`
            );
            (questionContainer || sectionContainer)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }, 360);
    }

    getNavigationOrder(questionKey: string): number {
        return this.navigationOrderByKey.get(questionKey) ?? -1;
    }

    getSectionNavigationOrder(section: SectionUI): number {
        const orders = section.questions
            .map(question => this.getNavigationOrder(question.key))
            .filter(order => order >= 0);
        return orders.length ? Math.min(...orders) : -1;
    }

    private buildNavigationOrder(): void {
        this.navigationOrderByKey.clear();
        let order = 0;
        this.sections.forEach(section => {
            this.getQuestions(section.number)?.forEach(question => {
                if (!question.disabled) {
                    this.navigationOrderByKey.set(question.key, order++);
                }
            });
        });
    }

    private isEmptyVisibleControl(control: HTMLElement): boolean {
        if (control.getClientRects().length === 0) return false;

        if (control instanceof HTMLInputElement) {
            if (control.type === 'checkbox' || control.type === 'radio') {
                return !control.checked;
            }
            return control.value.trim().length === 0;
        }

        if (control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement) {
            return control.value.trim().length === 0;
        }

        if (control instanceof HTMLButtonElement && control.classList.contains('categoryBox')) {
            const questionContainer = control.closest<HTMLElement>('[data-question-key]');
            const questionKey = questionContainer?.dataset['questionKey'];
            return !!questionKey && this.formService.getQuestion(questionKey) == null;
        }

        return false;
    }
}
